import { useAppForm } from "@features/forms/config";
import { FirebaseError } from "firebase/app";
import { z } from "zod";
import useSignIn from "./useSignIn";

const input = z.object({
  email: z.email("errorEmailInvalid"),
  password: z.string(),
});

export default function useSigninForm({
  onSuccess,
  defaultEmail = "",
}: {
  onSuccess?: () => void;
  defaultEmail?: string;
} = {}) {
  const signIn = useSignIn();

  return useAppForm({
    defaultValues: {
      email: defaultEmail,
      password: "",
    },
    validators: {
      onChange: ({ value }) => {
        const validation = input.safeParse(value);
        if (!validation.success) {
          return {
            fields: Object.fromEntries(
              validation.error.issues.map((issue) => [
                issue.path.join("."),
                issue.message,
              ]),
            ),
          };
        }
        return undefined;
      },
      onSubmitAsync: async ({ value }) => {
        try {
          await signIn({
            strategy: "email",
            email: value.email,
            password: value.password,
          });
          onSuccess?.();
        } catch (error) {
          if (error instanceof FirebaseError) {
            const { AuthErrorCodes } = await import("firebase/auth");
            if (error.code === AuthErrorCodes.INVALID_LOGIN_CREDENTIALS) {
              return { fields: { password: "invalidPassword" } };
            }
            if (error.code === AuthErrorCodes.USER_DELETED) {
              return { fields: { email: "userNotFound" } };
            }
            if (error.code === AuthErrorCodes.INVALID_PASSWORD) {
              return { fields: { password: "wrongPassword" } };
            }
            if (error.code === "auth/missing-password") {
              return { fields: { password: "missingPassword" } };
            }
            return "internalError";
          }
          if (error instanceof Error) {
            return error.message;
          }
          return "internalError";
        }
        return undefined;
      },
    },
  });
}
